import { useQuery } from '@tanstack/react-query';

import type { Product } from '@/components/products/detail/types';
import { PatientService } from '@/services/patient.service';
import { Logger } from '@/utils/logger';
import type { DELIVERY_OPTION, DELIVERY_OPTIONS } from '@montugroup/circuit-shared';
import type { IntervalDetails } from '@montugroup/circuit-shared/types/refill';

type OrderProduct = {
  quantity: number;
  product_id: number;
  order_id: number;
  id: number;
  Product: Product;
  intervalDetails: IntervalDetails | null;
};

export type DeliveryOption = {
  id: number;
  name: (typeof DELIVERY_OPTIONS)[DELIVERY_OPTION]['name'];
  price: number;
  deliveryRequirements: {
    identification: boolean;
    presence: boolean;
  };
};

export type PatientOrderPharmacyDetails = {
  id: number;
  name: string;
  address1: string;
  address2: string | null;
  city: string;
  state: string;
  zipCode: string;
};

export type PatientOrder = {
  order_code: string;
  order_date: string;
  id: number;
  total_price: string;
  OrderProducts: OrderProduct[];
  delivered: boolean;
  deliveredDate: string | null;
  shipped_date: string | null;
  dispensed_date: string | null;
  dispensing_fee: string;
  shipment_tracking_id: string | null;
  shipment_carrier: string | null;
  pharmacy?: PatientOrderPharmacyDetails;
  orderStatus: number;
  order_history_cancelled_date: string | null;
  intervalDetails: IntervalDetails | null;
  shippingTracking: {
    trackingUrl: string;
  } | null;
  deliveryOption?: DeliveryOption;
};

const logger = new Logger('useGetPatientOrders');

function useGetPatientOrders() {
  const { data, isError, isLoading } = useQuery({
    queryKey: ['patientOrders'],
    queryFn: async () => {
      try {
        const res = await PatientService.getPatientOrders();
        return res.data;
      } catch (error) {
        logger.error(error);
        throw new Error('Failed to fetch patient orders');
      }
    },
  });

  return {
    data,
    isError,
    isLoading,
  };
}

export default useGetPatientOrders;
